import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { DeliveryOptions } from "app/_components/checkout-line-item-delivery/checkout-line-item-delivery.component.state";
import { REGEX_PHONE_PATTERN } from "app/app.constants";
import { Branch } from "entities/branch";
import { CartResult, CartResultLineItem } from "entities/cart-result";
import { DeliveryTypes } from "entities/cart-search";
import { BillToInfo } from "entities/carts/bill-to-info";
import { SetDeliveryRequest } from "entities/carts/set-delivery-request";
import { UpdateCartRequest } from "entities/carts/update-cart-request";
import { addCreditCardTransactionResult } from "entities/checkout-billing-enums";
import { CountryCode } from "entities/country-code";
import { Customer } from "entities/customer";
import { DeliveryItems } from "entities/delivery-items";
import { OrderType } from "entities/order-type";
import { ShipToCustomer } from "entities/ship-to-customer";
import { StoredPaymentMethod } from "entities/stored-payment-method";
import { ToastType } from "entities/toast-type";
import { User } from "entities/user";
import { environment } from "environments/environment";
import { blankId } from "helpers/blank-id";
import { isCashCustomer } from "helpers/is-cash-customer";
import {
  catchError, combineLatest,
  debounceTime, distinctUntilChanged,
  filter, fromEventPattern, map, of,
  shareReplay,
  startWith,
  switchMap, tap,
  withLatestFrom
} from "rxjs";
import { CartService } from "services/cart.service";
import { CheckoutFormService } from "services/checkout-form.service";
import { FeatureFlagService } from "services/feature-flag.service";
import { LoaderService } from "services/loader.service";
import { ToastService } from "services/toast.service";
import { AppState } from "store/app-state";
import * as BranchSelectors from 'store/branch/branch.selectors';
import * as CartActions from "store/cart/cart.actions";
import * as CartSelectors from 'store/cart/cart.selectors';
import * as ConfigurationSelectors from 'store/configuration/configuration.selectors';
import * as CustomerActions from "store/customer/customer.actions";
import * as CustomerSelectors from "store/customer/customer.selectors";
import { CustomerState, SelectedCustomer } from "store/customer/customer.state";
import * as FeatureFlagSelectors from "store/feature-flags/feature-flags.selectors";
import { isFeatureActive } from "store/feature-flags/feature-flags.selectors";



@Injectable()
export class CheckoutFormEffects {
  private previousBillTo: string | null = null; // Track previous billTo value for payment switching logic

  reactOnFormChange$ = createEffect(() => combineLatest([
    this.checkoutFormService.checkoutForm.valueChanges,
    this.store.select(CartSelectors.selectCart),
  ]).pipe(
    debounceTime(300), // Add debouncing for rapid payment method changes
    distinctUntilChanged((prev, curr) => {
      // Custom comparison to prevent infinite loops
      if (!prev || !curr) return false;

      const [prevForm, prevCart] = prev;
      const [currForm, currCart] = curr;

      // Only trigger if there's a meaningful change in billTo or cart
      const billToChanged = prevForm?.paymentDetails?.billTo !== currForm?.paymentDetails?.billTo;
      const cartChanged = prevCart?.cartId !== currCart?.cartId;
      const billToCCChanged = prevForm?.paymentDetails?.billToCC !== currForm?.paymentDetails?.billToCC;

      return !billToChanged && !cartChanged && !billToCCChanged;
    }),
    filter(([_, cartData]) => (cartData?.cartId !== null || cartData?.cartId !== undefined || cartData !== undefined || cartData !== undefined )&& cartData?.cartId !== blankId),
    withLatestFrom(
      this.store.select(ConfigurationSelectors.selectUserInfo),
      this.store.select(BranchSelectors.selectIsWarehouseManaged),
      this.store.select(CustomerSelectors.selectPayers),
      this.store.select(FeatureFlagSelectors.isFeatureActive('PaymentCC')),
      this.store.select(CustomerSelectors.selectedCustomer),
    ),
    tap(([[formValue, cartData], userInfo, isWarehouseManaged, payers, isPaymenCC, customer]) => {

      // Track billTo changes for payment switching logic
      const currentBillTo = formValue?.paymentDetails?.billTo;

      // Only update previousBillTo if it actually changed to prevent unnecessary processing
      if (this.previousBillTo !== currentBillTo) {
        this.previousBillTo = currentBillTo;
      }

      this.customBehavior(userInfo, isWarehouseManaged, payers, isPaymenCC, customer);

      // For debugging the checkout form
      if(!environment.production && !environment.isRunningInUnitTest){
        console.log("[Form Log] valueChange value:", formValue);
        console.log("[Form Log] Validators:",this.checkoutFormService.getAllValidators());
        console.log("[Form Log] CartData:", cartData);
        console.log("[Form Log] Form Valid:", this.checkoutFormService.checkoutForm.valid);
        console.log("[Form Log] Payment Details:", formValue.paymentDetails);
        console.log("[Form Log] BillTo Current:", formValue.paymentDetails?.billTo);
        console.log("[Form Log] BillTo Previous:", this.previousBillTo);
      }
    }),
  ), {dispatch: false});

  // Fires when user updates the checkout form
  updateDatabase$ = createEffect(() =>
    this.checkoutFormService.checkoutForm.valueChanges
    .pipe(      filter((x) => {
        const hasCartData = x.shippingAndDelivery.cartData !== null;
        const isFormDirty = this.checkoutFormService.checkoutForm.dirty;

        return hasCartData && isFormDirty;
      }),
      distinctUntilChanged(),
      debounceTime(1),
      withLatestFrom(
        this.store.select(CustomerSelectors.selectPayers),
        this.store.select(CustomerSelectors.selectShipTo),
        this.store.select(CustomerSelectors.selectStoredPaymentMethods),
        this.store.select(BranchSelectors.selectedBranch),
        this.store.select(CartSelectors.selectDeliveryItems)
      ),
      switchMap(([formGroupValue, payers, shipToCustomer, storedPaymentMethods, branch, deliveryItems]) => {
        this.loaderService.checkoutLoaderSubject.next(true);
        const updateCartRequest = this.getUpdateCartPayload(formGroupValue, payers, shipToCustomer, storedPaymentMethods, branch);
        return this.cartService.updateCart({...updateCartRequest, includeCart: true}).pipe(
          tap((cart) => {
            this.loaderService.checkoutLoaderSubject.next(false);
            this.checkoutFormService.checkoutForm.markAsPristine();
            this.store.dispatch(CartActions.updateCartSuccess({cart, deliveryItems}));
            this.checkoutFormService.checkoutForm.updateValueAndValidity();
          }),
          catchError((error) => {
            this.loaderService.checkoutLoaderSubject.next(false);
            this.toastService.showToast(error, ToastType.Error);
            this.store.dispatch(CartActions.updateCartFailed({error}));
            this.checkoutFormService.checkoutForm.markAsPristine();
            this.checkoutFormService.checkoutForm.updateValueAndValidity();
            return of(error);
          }),
        );
      })
  ), {dispatch: false});

  updateCartOnSubmit$ = createEffect(() => this.actions.pipe(
    ofType(CartActions.preSubmitTransaction),
    withLatestFrom(
      this.store.select(CustomerSelectors.selectPayers),
      this.store.select(CustomerSelectors.selectShipTo),
      this.store.select(CustomerSelectors.selectStoredPaymentMethods),
      this.store.select(BranchSelectors.selectedBranch)
    ),
    switchMap(([{clearShoppingBasket, transactionType}, payers, shipToCustomer, storedPaymentMethods, branch]) => {
      const updateCartParams = this.getUpdateCartPayload(this.checkoutFormService.checkoutForm.value, payers, shipToCustomer, storedPaymentMethods, branch);
      return this.cartService.updateCart({...updateCartParams, includeCart: false}).pipe(
        map(() => CartActions.submitTransaction({clearShoppingBasket, transactionType})),
        catchError((error) => of(CartActions.submitOrderFailed({error})))
      );
    })
  ));

  updateDefaultPaymentOnPayerChange$ = createEffect(() => this.actions.pipe(
    ofType(CustomerActions.updateCheckoutWithSelectedPayer),
    withLatestFrom(
      this.store.select(CustomerSelectors.selectPayers)
    ),
    switchMap(([_, payers]) => {
      const billTo = this.checkoutFormService.checkoutForm.get('paymentDetails.billTo');
      const payer = payers.find(x => x.isDefault === true && !x.payerNotValidInBranch);
      if (payer) {
        billTo.setValue(payer.addressNumber, {emitEvent: false});
      }
        return of();
    })
  ), {dispatch: false});

  inBaseUrl$ = fromEventPattern(
    handler => this.router.events.subscribe(handler)
  ).pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.urlAfterRedirects),
    map(url => this.isInBaseRoute(url)),
    shareReplay(1)
  );


  private isInBaseRoute(url: string): boolean {
    // Assuming your base route is '/'
    return url === '/' || url === '';
  }


  updateCheckoutForm$ = createEffect(() => combineLatest(
    [this.store.select(CartSelectors.selectCart),
     this.activatedRoute.queryParams,
     this.inBaseUrl$,
     this.actions.pipe(ofType(CartActions.getPartBinLocationsSuccess), startWith(null)),
     this.actions.pipe(ofType(CartActions.setDeliverySuccess), startWith(null))
    ]
  ).pipe(
    filter(([cartData, _, isBaseUrl]) => cartData?.cartId !== null && cartData?.cartId !== blankId && !isBaseUrl),
    distinctUntilChanged(),
    withLatestFrom(
      this.store.select(CartSelectors.selectDeliverySummary),
      this.store.select(CustomerSelectors.customerState),
      this.store.select(ConfigurationSelectors.isInternal),
      this.loaderService.checkoutLoaderSubject,
      this.store.select(ConfigurationSelectors.isCanadaUser),
      this.store.select(isFeatureActive('ShippingManagement'))
    ),
    // This Prevents the form from being overwritten as you are typing
    filter(() => this.checkoutFormService.checkoutForm.pristine),
    tap(([[cartData, queryParams], deliverySummary, customer, isInternal, isLoading, isCanadaUser, shippingManagementEnabled]) =>
      this.patchForm(cartData, customer, queryParams, isInternal, isCanadaUser, shippingManagementEnabled)
    ),
    map(([cartData, _]) => {
      this.store.dispatch(CartActions.cacheCart());
      return cartData;
    })
  ), {dispatch: false});


  disableDeliveryOptionsWhenFreightLoading$ = createEffect(() => this.store.select(CartSelectors.selectIsShippingRatesLoading).pipe(
    tap((isLoading) => {
      const deliveryTypeControl = this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.deliveryType;
      if(isLoading){
        deliveryTypeControl.disable({emitEvent: false});
      } else {
        deliveryTypeControl.enable({emitEvent: false});
      }
    })
  ), {dispatch: false});

  clearCart$ = createEffect(() => this.actions.pipe(
    ofType(CustomerActions.clearCustomer),
    tap(() => {
      this.store.dispatch(CartActions.clearCartData());
    })
  ), {dispatch: false});

  constructor(
    @Inject(REGEX_PHONE_PATTERN) public regexPhonePattern: string,
    private cartService: CartService,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private checkoutFormService: CheckoutFormService,
    private store: Store<AppState>,
    private loaderService: LoaderService,
    private featureFlagService: FeatureFlagService,
    private actions: Actions
  ) { }

  customBehavior(user: User, isWarehouseManaged: boolean, payers: Customer[], isPaymentCC: boolean, customer: SelectedCustomer){
    const billToCCControl = this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC');
    const optInControl = this.checkoutFormService.checkoutForm.get('smsOpt.optIn');
    const optInContactNameControl = this.checkoutFormService.checkoutForm.get('smsOpt.contactName');
    const optInMobileNumberControl = this.checkoutFormService.checkoutForm.get('smsOpt.mobileNumber');
    const specialInstructionControl = this.checkoutFormService.checkoutForm.get('additionalDetails.specialInstructions');

    if(
      !!this.checkoutFormService.checkoutForm.value?.shippingAndDelivery?.cartData?.orderNumber &&
      (this.checkoutFormService.checkoutForm.value?.shippingAndDelivery?.cartData?.isLocked &&
      this.checkoutFormService.checkoutForm.value?.shippingAndDelivery?.cartData?.updatedById !== user?.id)
      ){
      specialInstructionControl.disable({emitEvent: false});
    } else {
      specialInstructionControl.enable({emitEvent: false});
    }

    if(optInControl.value){
      optInContactNameControl.enable({emitEvent: false});
      optInMobileNumberControl.enable({emitEvent: false});
    } else {
      optInContactNameControl.disable({emitEvent: false});
      optInMobileNumberControl.disable({emitEvent: false});
    }    const currentBillTo = this.checkoutFormService.checkoutForm.value.paymentDetails.billTo;

    // Enable credit card control for both CASH-CCOnline and PPFS-TREVIPAY payment options
    if(currentBillTo !== "CASH-CCOnline" && currentBillTo !== "PPFS-TREVIPAY (M100000)"){
      billToCCControl.disable({emitEvent: false}); // Keep emitEvent: false to prevent infinite loops
    } else {
      billToCCControl.enable({emitEvent: false}); // Keep emitEvent: false to prevent infinite loops
    }

    if(!isWarehouseManaged) {
      this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.deliveryItems.setValue(DeliveryItems.all, { emitEvent: false });
    }

    this.setBillToForMatchingMSCPayer(customer, payers, isPaymentCC);

  }

  setBillToForMatchingMSCPayer(customer: SelectedCustomer, payers: Customer[], isPaymentCC: boolean) {
    const payerNumber = customer?.mscPayer?.payerNumber;
    let customers: Customer[] = [];
    if (!isPaymentCC) {
      customers = payers.filter( payer => !payer.addressNumber.startsWith("CASH-") );
    } else {
      customers = payers;
    }
    let matching;
    if(!!customers && customers.length > 0 && !!payerNumber) {
      // Find the bill-to option that matches the MSC payer number
      const matchingOption = customers.find(
        item => item.addressNumber === payerNumber
      );

      matching = matchingOption && !matchingOption.payerNotValidInBranch ? matchingOption : null;
    }

    // Only auto-set the billTo if:
    // 1. There's a matching MSC payer, AND
    // 2. The current billTo is null/empty (initial state), OR
    // 3. The current billTo is already the matching address (to maintain consistency)
    if(matching) {
      const billTo = this.checkoutFormService.checkoutForm.get('paymentDetails.billTo');
      const currentBillTo = billTo.value;
      // Only set if it's initial load (null/empty) or already set to this value
      if (!currentBillTo || currentBillTo === '' || currentBillTo === matching.addressNumber) {
        billTo.setValue(matching.addressNumber, {emitEvent: false});
      }
    }
  }

  patchPostalCode(currentShipToZip: string, cartDataZip: string) {
    if (cartDataZip === `${currentShipToZip}-0000`) {
      return cartDataZip;
    }
    return currentShipToZip;
  }

  patchForm(
    cartData: CartResult,
    customer: CustomerState,
    paymentQueryParams: Params,
    isUserInternal: boolean,
    isCanadaUser: boolean,
    shippingManagementEnabled: boolean
  ): void {
    // Prevent patching with invalid/empty data to avoid race conditions
    if (!cartData || !customer || !customer.selectedCustomer) {
      return;
    }
    const poNumber = cartData.poNumber ?? customer.selectedCustomer.customerPONumber;
    const addedCCResult = this.getCCResult(paymentQueryParams['TRANSUCCESS']);
    const i2p = this.getI2Pay(cartData);
    const forAllItems = this.isSameDeliveryForAllLineItems(cartData.lineItems);

    // Preserve user's billTo selection if they've manually changed it
    const currentBillTo = this.checkoutFormService.checkoutForm.get('paymentDetails.billTo')?.value;
    const preserveBillTo = currentBillTo && currentBillTo !== cartData.billToAddressNo;
    const billToValue = preserveBillTo ? currentBillTo : cartData.billToAddressNo;

    // Handle shipTo data with race condition protection
    const shipToForm = this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.shipTo;
    const currentShipToData = {
      ...shipToForm.value,
      postalCode: this.patchPostalCode(shipToForm.value.postalCode, cartData.zip)
    }

    // Determine appropriate shipTo data based on customer type and data quality
    const isCashCustomer = customer.selectedCustomer.rewardNumber === "200000";
    const hasUserData = !!(currentShipToData.customerName?.trim() &&
                          currentShipToData.streetAddress?.trim() &&
                          currentShipToData.phoneNumber?.trim());
    const hasValidCartAddress = !!(cartData.city && cartData.state && cartData.zip);

    let shipToData;
    if (isCashCustomer && !hasUserData && hasValidCartAddress) {
      // Cash customer without user data - use branch address data
      shipToData = {
        addressNumber: "0000200000",
        city: cartData.city,
        customerName: cartData.name1,
        customerName2: cartData.name2,
        rewardNumber: cartData.rewardNumber,
        mscAccountNumber: cartData.msc,
        phoneNumber: cartData.phone,
        postalCode: cartData.zip,
        salesperson: cartData.salesPerson,
        state: cartData.state,
        streetAddress: cartData.address,
        isEdited: cartData.isAlternateAddressEdited,
        isCanadaUser: isCanadaUser,
        shippingManagementEnabled: shippingManagementEnabled
      };
    } else if (hasUserData) {
      // Preserve user-entered data
      shipToData = currentShipToData;
    } else {
      // Default case - use cart data
      shipToData = {
        addressNumber: isCashCustomer ? "0000200000" : cartData.shipToAddressNo,
        city: cartData.city,
        customerName: cartData.name1,
        customerName2: cartData.name2,
        rewardNumber: cartData.rewardNumber,
        mscAccountNumber: cartData.msc,
        phoneNumber: cartData.phone,
        postalCode: cartData.zip,
        salesperson: cartData.salesPerson,
        state: cartData.state,
        streetAddress: cartData.address,
        isEdited: cartData.isAlternateAddressEdited,
        isCanadaUser: isCanadaUser,
        shippingManagementEnabled: shippingManagementEnabled
      };
    }

    this.checkoutFormService.checkoutForm.patchValue({
      checkoutDetails: {
        poNumber,
        unitNumber: cartData.unitNumber,
        transactionType: cartData.transactionType,
        orderChannel: cartData.orderChannel
      },
      shippingAndDelivery: {
        deliveryItems: cartData.deliveryBatchType,
        deliveryType: cartData.deliveryType?.toLowerCase(),
        cartData: { ...cartData, isUserInternal },
        shipTo: shipToData, // Use preserved shipTo data
        freightdeliveryoption: cartData?.shippingRate?.service?? null,
        shipComplete: cartData.shipComplete
      },
      paymentDetails: {
        selectedPayment: cartData.paymentSelection,
        billTo: billToValue, // Use preserved billTo value
        billToCC: cartData.paymentSelection,
        i2p,
        i2pEmail: cartData.i2pEmail,
        i2pSms: cartData.i2pSmsNumber,
        addedCCResult
      },
      additionalDetails: {
        altEmail: cartData.alternateEmail === null ? "" : cartData.alternateEmail,
        specialInstructions: cartData.specialInstruction
      },
      smsOpt: {
        optIn: cartData?.smsOptIn,
        contactName: cartData?.smsContactName,
        mobileNumber: cartData?.smsMobileNumber
      },
      customer: customer?.selectedCustomer
    },
      {emitEvent: true, onlySelf: true}
    );


    if (!cartData.smsOptIn) {
      this.disableSmsOptInFields();
    } else {
      this.enableSmsOptInFields();
    }

    this.checkoutFormService.checkoutForm.updateValueAndValidity({emitEvent: true});
    this.checkoutFormService.checkoutForm.markAsPristine();
  }

  disableSmsOptInFields() {
    this.checkoutFormService.checkoutForm['controls'].smsOpt['controls'].contactName.disable({emitEvent: false});
    this.checkoutFormService.checkoutForm['controls'].smsOpt['controls'].mobileNumber.disable({emitEvent: false});
  }

  enableSmsOptInFields() {
    this.checkoutFormService.checkoutForm['controls'].smsOpt['controls'].contactName.enable({emitEvent: false});
    this.checkoutFormService.checkoutForm['controls'].smsOpt['controls'].mobileNumber.enable({emitEvent: false});
  }

  getI2Pay(cartData: any): string {
    let i2p = null;
    if (cartData.i2pEmail !== null && cartData.i2pEmail !== undefined && cartData.i2pEmail !== "") {
      i2p = "email";
    }
    if (cartData.i2pSmsNumber !== null && cartData.i2pSmsNumber !== undefined && cartData.i2pSmsNumber !== "") {
      i2p = "sms";
    }
    if (i2p === null && cartData.paymentSelection === 'CCI2P') {
      i2p = this.checkoutFormService.checkoutForm.value.paymentDetails.i2p;
    }
    return i2p;
  }

  getCCResult(queryParam: string): string | null {
    let addedCCResult: string;

    switch(queryParam) {
      case 'TRUE':
        addedCCResult = addCreditCardTransactionResult.Added;
        break;
      case 'FALSE':
        addedCCResult = addCreditCardTransactionResult.Failed;
        break;
      default:
        addedCCResult = null;
        break;
    }

    return addedCCResult;
  }

  isSameDeliveryForAllLineItems(lineItems: CartResultLineItem[]) {
    return Array
      .from(new Set(lineItems?.filter(x => x.partNumber !== "FREIGHT" && x.partNumber !== "DELIVERY:90")
      .map(x => x.deliveryOption)))
      .length <= 1;
  }

  getDeliveryItemsTab(cartData: CartResult, deliveryOptions: string[], isInternalUser: boolean) {
    let tab = (cartData.deliveryType || !deliveryOptions.length) ? DeliveryItems.all : DeliveryItems.lineByLine;
    const deliveryItems = this.deliveryItems();
    if (deliveryItems !== DeliveryItems.all) {
      tab = deliveryItems;
    }
    if (!isInternalUser) {
      tab = DeliveryItems.all;
    }
    return tab;
  }

  deliveryItems(): DeliveryItems | undefined {
    const x = this.checkoutFormService.checkoutForm.get('shippingAndDelivery')?.get('deliveryItems')?.value;
    return x;
  }

  getPaymentTokenFromQueryParams(queryParams: Params){
    const newStoredPaymentMethod: StoredPaymentMethod = {
      id: "",
      paymentProviderName: "",
      paymentToken: queryParams['REPTOKEN'],
      processorToken: queryParams['TRANSARMORTOKEN'],
      customerId: "",
      driversLicenseNumber: "",
      driversLicenseState: "",
      creditCardAccountExpiration: queryParams['CCEXP'],
      creditCardAccountName: queryParams['REPCCNAME'],
      creditCardNickname: "",
      creditCardAccountNumber: queryParams['CCACCOUNT'],
      tokenBrand: "",
      creditCardType: queryParams['CCCARDTYPE'],
      checkingAccountName: "",
      checkingAccountRoutingNumber: "",
      checkingAccountNumber: "",
      isCheckingAccountBusiness: "",
      isCheckingAccountSavings: ""
    };
    return newStoredPaymentMethod;
  }

  getUpdateCartPayload(formGroupValue: any, payers: Customer[], shipToCustomer: ShipToCustomer, storedPaymentMethods: StoredPaymentMethod[], branch: Branch) {
    const cartData: CartResult = formGroupValue.shippingAndDelivery.cartData;

    // Extract complex logic into smaller methods
    const setDeliveryRequest = this.buildSetDeliveryRequest(formGroupValue, cartData);
    const updateBillToRequest = this.buildBillToRequest(formGroupValue, payers);
    const updateShipToRequest = this.buildShipToRequest(formGroupValue, shipToCustomer, cartData, branch);
    const { i2pEmail, i2pSmsNumber } = this.processInviteToPayOptions(formGroupValue, setDeliveryRequest);
    const storedPaymentMethod = this.findStoredPaymentMethod(formGroupValue, storedPaymentMethods);

    const updateCartRequest = this.buildUpdateCartRequest(
      formGroupValue,
      setDeliveryRequest,
      updateBillToRequest,
      updateShipToRequest,
      i2pEmail,
      i2pSmsNumber,
      storedPaymentMethod
    );

    // Handle payment method clearing logic
    this.handlePaymentMethodClearing(updateCartRequest, updateBillToRequest);

    // Handle form state management
    this.handleFormStateManagement(updateBillToRequest);

    this.handleIsEdited(updateCartRequest);

    this.handleDefaultCashCustomerLogic(updateCartRequest, cartData, branch);

    return updateCartRequest;
  }

  private handleDefaultCashCustomerLogic(updateCartRequest: UpdateCartRequest, cartResult: CartResult, branch: Branch){
    const isPickupDeliveryOptionSelected = updateCartRequest.setDeliveryRequest?.headerDeliveryType && [
          DeliveryTypes.pickup.toString(),
          DeliveryTypes.adHoc.toString(),
          DeliveryTypes.selfPick.toString(),
          DeliveryTypes.fcKnown.toString()].includes(updateCartRequest.setDeliveryRequest.headerDeliveryType);

        // Only auto-populate for cash customers (200000 or 0000200000)
        const isCashCustomer = cartResult.rewardNumber === "200000" || cartResult.rewardNumber === "0000200000";

        if (
          isCashCustomer &&
          (
            cartResult.shipToAddressNo === null ||
            cartResult.shipToAddressNo === '' ||
            cartResult.shipToAddressNo === undefined
          ) &&
          isPickupDeliveryOptionSelected &&
          branch?.city && branch?.state && branch?.zip
        ) {
          updateCartRequest.updateShipToRequest = {
            addressNumber: cartResult.rewardNumber,
            city: branch.city,
            customerName: "",
            customerName2: "",
            mscAccountNumber: null,
            phoneNumber: "",
            postalCode: branch.zip,
            state: branch.state,
            streetAddress: "",
            isCanadaUser: branch.country === "CA"
          } as ShipToCustomer;
        }
  }

  private buildSetDeliveryRequest(formGroupValue: any, cartData: CartResult): SetDeliveryRequest | null {
    const rewardNumber = cartData.rewardNumber;
    let deliveryTypes = null;

    if (this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems').value === DeliveryItems.all) {
      deliveryTypes = cartData?.lineItems?.map((item) => ({
        cartItemId: item.cartItemId,
        deliveryOption: item.lineId === 801 || item.lineId === 901 ? '' : cartData.deliveryType
      })).reduce((acc, item) => ({ ...acc, [item.cartItemId]: formGroupValue.shippingAndDelivery.deliveryType}), {});
    } else {
      deliveryTypes = cartData?.lineItems.map((item) => ({
        cartItemId: item.cartItemId,
        deliveryOption: item.lineId === 801 || item.lineId === 901 ? '' : DeliveryOptions.find(x => x.key === item.deliveryOption)?.option.toLowerCase()
      })).reduce((acc, item) => ({ ...acc, [item.cartItemId]: item.deliveryOption }), {});
    }

    const setDeliveryRequest: SetDeliveryRequest = {
      addDeliveryFees: true,
      branchCode: cartData.branchCode,
      cartId: cartData.cartId,
      headerDeliveryType: formGroupValue.shippingAndDelivery.deliveryType as DeliveryTypes ?? cartData.deliveryType as DeliveryTypes,
      rewardNumber,
      deliveryTypes,
      lineByLine: this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems').value === DeliveryItems.lineByLine
    };

    return this.validateSetDeliveryrequest(setDeliveryRequest);
  }

  private buildBillToRequest(formGroupValue: any, payers: Customer[]): BillToInfo {
    const billToRewardNumber = formGroupValue.paymentDetails.billTo;
    const billTocustomer: Customer = payers.find(payer => payer?.addressNumber === billToRewardNumber);

    return billToRewardNumber === null ? null : {
      addressNumber: billToRewardNumber,
      customerName: billTocustomer?.customerName ?? ''
    };
  }

  private buildShipToRequest(formGroupValue: any, shipToCustomer: ShipToCustomer, cartData: CartResult, branch: Branch): ShipToCustomer {
    const cartShipTo = formGroupValue?.shippingAndDelivery?.shipTo;
    let updateShipToRequest: ShipToCustomer = {...cartShipTo};

    // Handle cash customer shipping logic
    updateShipToRequest = this.handleCashCustomerShipping(updateShipToRequest, cartData, branch);

    // Handle non-cash customer shipping
    if (!isCashCustomer(shipToCustomer?.addressNumber) && !!shipToCustomer?.addressNumber) {
      updateShipToRequest = {...shipToCustomer};
    }

    // Handle manual ship-to modifications
    updateShipToRequest = this.handleManualShipToModifications(updateShipToRequest, cartData);

    return { ...updateShipToRequest, isCanadaUser: CountryCode.CA === branch?.country };
  }

  private handleCashCustomerShipping(updateShipToRequest: ShipToCustomer, cartData: CartResult, branch: Branch): ShipToCustomer {
    const hasDeliveryOption = ["delivery", "shipto", "shipdirect"].includes(cartData.deliveryType);
    const isPickupDeliveryOption = ["pickup", "adhoc", "selfpick", "fcknown"].includes(cartData.deliveryType);

    if (!this.doesAtLeastOneFieldHaveValue(updateShipToRequest) && isCashCustomer(updateShipToRequest.rewardNumber)) {
      if (isPickupDeliveryOption) {
        updateShipToRequest.city = branch.city;
        updateShipToRequest.state = branch.state;
        updateShipToRequest.postalCode = branch.zip;
      } else if (hasDeliveryOption) {
        updateShipToRequest = {} as ShipToCustomer;
      }
      // If deliveryType is undefined/null, don't clear the existing data
    }

    return updateShipToRequest;
  }

  private handleManualShipToModifications(updateShipToRequest: ShipToCustomer, cartData: CartResult): ShipToCustomer {
    const shipToControls = this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.shipTo;

    const shipToTouched = {
      addressNumber: shipToControls.controls.addressNumber.dirty,
      city: shipToControls.controls.city.dirty,
      customerName: shipToControls.controls.customerName.dirty,
      customerName2: shipToControls.controls.customerName2.dirty,
      rewardNumber: shipToControls.controls.rewardNumber.dirty,
      isEdited: shipToControls.controls.isEdited.dirty,
      mscAccountNumber: shipToControls.controls.mscAccountNumber.dirty,
      phoneNumber: shipToControls.controls.phoneNumber.dirty,
      postalCode: shipToControls.controls.postalCode.dirty,
      salesperson: shipToControls.controls.salesperson.dirty,
      state: shipToControls.controls.state.dirty,
      streetAddress: shipToControls.controls.streetAddress.dirty
    };

    const shipToManuallyModified = new Set(Object.values(shipToTouched)).size > 1;
    const anyFieldsDiffer = this.checkIfShipToFieldsDiffer(shipToControls.value, cartData);

    if (shipToManuallyModified) {
      const updatedShipTo = shipToControls.value as ShipToCustomer;
      updateShipToRequest = {...updatedShipTo, isEdited: shipToManuallyModified};
      this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.shipTo.controls.isEdited.setValue(shipToManuallyModified, {emitEvent: false});
    } else if (anyFieldsDiffer) {
      const updatedShipTo = shipToControls.value as ShipToCustomer;
      updateShipToRequest = {...updatedShipTo};
    }

    return updateShipToRequest;
  }

  private checkIfShipToFieldsDiffer(shipToValue: any, cartData: CartResult): boolean {
    return shipToValue.customerName !== cartData.customerName ||
           shipToValue.streetAddress !== cartData.address ||
           shipToValue.city !== cartData.city ||
           shipToValue.state !== cartData.state ||
           shipToValue.phoneNumber !== cartData.phone ||
           shipToValue.mscAccountNumber !== cartData.msc;
  }

  private processInviteToPayOptions(formGroupValue: any, setDeliveryRequest: SetDeliveryRequest | null): { i2pEmail: string | null, i2pSmsNumber: string | null } {
    let i2pEmail = formGroupValue.paymentDetails.i2pEmail;
    let i2pSmsNumber = formGroupValue.paymentDetails.i2pSms;

    // Clear conflicting I2P options
    if ((formGroupValue.paymentDetails.i2p === "email" && formGroupValue.paymentDetails.i2pSms !== null && formGroupValue.paymentDetails.i2pSms !== "") ||
        (formGroupValue.paymentDetails.i2p === "sms" && formGroupValue.paymentDetails.i2pEmail !== null && formGroupValue.paymentDetails.i2pEmail !== "")) {
      i2pEmail = null;
      i2pSmsNumber = null;
    }

    // Clear I2P for front counter/self-pick items (only if setDeliveryRequest is not null)
    if (setDeliveryRequest) {
      const hasFrontCounterSelfPickOrBasketReturnItems =
        (setDeliveryRequest.headerDeliveryType === 'adhoc' || setDeliveryRequest.headerDeliveryType === 'selfpick') ||
        (setDeliveryRequest.deliveryTypes && Object.values(setDeliveryRequest.deliveryTypes).some(x => x === 'adhoc' || x === 'selfpick'));

      if (hasFrontCounterSelfPickOrBasketReturnItems) {
        i2pEmail = null;
        i2pSmsNumber = null;
      }
    }

    return { i2pEmail, i2pSmsNumber };
  }

  private findStoredPaymentMethod(formGroupValue: any, storedPaymentMethods: StoredPaymentMethod[]): StoredPaymentMethod | null {
    return storedPaymentMethods.find(
      (paymentMethod: StoredPaymentMethod) => paymentMethod.id === formGroupValue.paymentDetails.billToCC
    ) ?? null;
  }

  private buildUpdateCartRequest(
    formGroupValue: any,
    setDeliveryRequest: SetDeliveryRequest,
    updateBillToRequest: BillToInfo,
    updateShipToRequest: ShipToCustomer,
    i2pEmail: string | null,
    i2pSmsNumber: string | null,
    storedPaymentMethod: StoredPaymentMethod | null
  ): UpdateCartRequest {
    return {
      alternateEmail: formGroupValue.additionalDetails.altEmail,
      cartId: formGroupValue.shippingAndDelivery.cartData?.cartId,
      poNumber: formGroupValue.checkoutDetails?.poNumber?.toUpperCase(),
      specialInstruction: formGroupValue.additionalDetails.specialInstructions,
      unitNumber: formGroupValue.checkoutDetails?.unitNumber,
      setDeliveryRequest,
      updateBillToRequest,
      updateShipToRequest,
      transactionType: formGroupValue.checkoutDetails.transactionType?.toString(),
      orderChannel: formGroupValue.checkoutDetails.orderChannel?.toString(),
      i2pEmail,
      i2pSmsNumber,
      paymentMethod: formGroupValue.paymentDetails.billToCC,
      storedToken: storedPaymentMethod,
      contactName: formGroupValue.smsOpt?.optIn ? formGroupValue.smsOpt?.contactName : null,
      mobileNumber: formGroupValue.smsOpt?.optIn ? formGroupValue.smsOpt?.mobileNumber : null,
      optIn: formGroupValue.smsOpt?.optIn,
      shipComplete: formGroupValue.shippingAndDelivery?.shipComplete ?? null,
      deliveryBatchType: formGroupValue.shippingAndDelivery.deliveryItems
    } as UpdateCartRequest;
  }

  private handlePaymentMethodClearing(updateCartRequest: UpdateCartRequest, updateBillToRequest: BillToInfo): void {
    const currentBillTo = updateBillToRequest?.addressNumber;
    const isCreditCardSwitch =
      (currentBillTo === "CASH-CCOnline" || this.previousBillTo === "CASH-CCOnline") &&
      (currentBillTo === "PPFS-TREVIPAY (M100000)" || this.previousBillTo === "PPFS-TREVIPAY (M100000)");

    // Debug logging for payment switching
    if (!environment.production && !environment.isRunningInUnitTest) {
      console.log("[Payment Log] Current BillTo:", currentBillTo);
      console.log("[Payment Log] Previous BillTo:", this.previousBillTo);
      console.log("[Payment Log] Is Credit Card Switch:", isCreditCardSwitch);
      console.log("[Payment Log] Payment Method Before:", updateCartRequest.paymentMethod);
    }

    // Don't clear payment methods when switching between credit card payment options
    if (!isCreditCardSwitch &&
        ((updateBillToRequest?.addressNumber !== "CASH-CCOnline" && updateCartRequest.paymentMethod !== null) ||
         updateCartRequest.transactionType === OrderType.Quote)) {
      updateCartRequest.paymentMethod = null;
      updateCartRequest.storedToken = null;
      updateCartRequest.i2pEmail = null;
      updateCartRequest.i2pSmsNumber = null;

      if (!environment.production && !environment.isRunningInUnitTest) {
        console.log("[Payment Log] Payment method cleared due to non-credit card billTo");
      }
    }

    // Debug logging for final payment state
    if (!environment.production && !environment.isRunningInUnitTest) {
      console.log("[Payment Log] Payment Method After:", updateCartRequest.paymentMethod);
    }

    // Update previous billTo tracking
    this.previousBillTo = currentBillTo;
  }

  private handleFormStateManagement(updateBillToRequest: BillToInfo): void {
    const billToCCControl = this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC');
    const billToCCValue = billToCCControl?.value;

    if (billToCCValue !== null && billToCCValue !== '' &&
        updateBillToRequest?.addressNumber !== "CASH-CCOnline" &&
        updateBillToRequest?.addressNumber !== "PPFS-TREVIPAY (M100000)") {
      billToCCControl?.setValue('', {emitEvent: false});
      this.checkoutFormService.checkoutForm.markAsPristine();
    }

    if (billToCCValue !== null && billToCCValue !== '' &&
        updateBillToRequest?.addressNumber === "CASH-CCOnline") {
      this.checkoutFormService.checkoutForm.markAsPristine();
    }
  }

  validateSetDeliveryrequest(request: SetDeliveryRequest): SetDeliveryRequest | null {
    if(request.headerDeliveryType === null){
      return null;
    }
    return request;
  }

  doesAtLeastOneFieldHaveValue(shipToCustomer: ShipToCustomer) {
    if(Object.values(shipToCustomer).length === 0){
      return false;
    }
    const cityHasValue = shipToCustomer.city !== null && shipToCustomer.city !== '';
    const customerNameHasValue = shipToCustomer.customerName !== null && shipToCustomer.customerName !== '';
    const customerName2HasValue = shipToCustomer.customerName2 !== null && shipToCustomer.customerName2 !== '';
    const mscAccountNumberHasValue = shipToCustomer.mscAccountNumber !== null && shipToCustomer.mscAccountNumber !== '';
    const phoneNumberHasValue = shipToCustomer.phoneNumber !== null && shipToCustomer.phoneNumber !== '';
    const stateHasValue = shipToCustomer.state !== null && shipToCustomer.state !== '';
    const streetAddressHasValue = shipToCustomer.streetAddress !== null && shipToCustomer.streetAddress !== '';
    const postalCodeHasValue = shipToCustomer.postalCode !== null && shipToCustomer.postalCode !== '';
    return cityHasValue ||
           customerNameHasValue ||
           customerName2HasValue ||
           mscAccountNumberHasValue ||
           phoneNumberHasValue ||
           stateHasValue ||
           streetAddressHasValue ||
           postalCodeHasValue;
  }


  handleIsEdited(updateCartRequest: UpdateCartRequest) {
    updateCartRequest.updateShipToRequest.isEdited = Boolean(updateCartRequest.updateShipToRequest.isEdited);
  }

}
